/**
 * Pattern Universe Viewer
 *
 * Displays the universe of stocks being monitored for a pattern.
 * Shows symbol list, sector distribution, and allows refresh.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, RefreshCw, Eye, TrendingUp, ChevronDown, ChevronUp, ArrowUpDown, Shield, Zap, Settings2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

import type { PatternUniverse as PatternUniverseType, PatternUniverseStats, AiGuardSettings, SignalGuardMode } from '../../types/titan';
import { patternsApi, settingsApi, ApiError } from '../../lib/api';
import { ClickableTicker } from '../TradingViewModal';

// ─── Props ────────────────────────────────────────────────────────────────────

interface PatternUniverseProps {
  patternId: string;
  patternName: string;
  isActive: boolean;
}

// ─── Pure formatting helpers ──────────────────────────────────────────────────

const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '-';
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toFixed(0);
};

const formatPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined) return '-';
  return `$${price.toFixed(2)}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'America/New_York',
  });
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTOR_COLORS: Record<string, string> = {
  Technology: 'bg-blue-500/10 text-blue-700',
  Healthcare: 'bg-emerald-500/10 text-emerald-700',
  'Financial Services': 'bg-amber-500/10 text-amber-700',
  'Consumer Cyclical': 'bg-pink-500/10 text-pink-700',
  'Consumer Defensive': 'bg-purple-500/10 text-purple-700',
  Energy: 'bg-orange-800/10 text-orange-800',
  Industrials: 'bg-slate-500/10 text-slate-700',
  'Basic Materials': 'bg-cyan-500/10 text-cyan-700',
  Utilities: 'bg-lime-500/10 text-lime-700',
  'Real Estate': 'bg-indigo-500/10 text-indigo-700',
  'Communication Services': 'bg-rose-500/10 text-rose-700',
};

// ─── Types ────────────────────────────────────────────────────────────────────

type Order = 'asc' | 'desc';
type OrderBy = 'symbol' | 'price' | 'volume' | 'sector' | 'market_cap';

interface SymbolRow {
  symbol: string;
  price: number;
  volume: number;
  sector: string;
  industry: string;
  market_cap: number | null;
}

// ─── SortableHeader — defined OUTSIDE parent to prevent recreation on render ──

interface SortableHeaderProps {
  field: OrderBy;
  children: React.ReactNode;
  className?: string;
  orderBy: OrderBy;
  order: Order;
  onSort: (field: OrderBy) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ field, children, className, orderBy, order, onSort }) => (
  <TableHead className={className}>
    <button
      type="button"
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      onClick={() => onSort(field)}
    >
      {children}
      <ArrowUpDown className={`w-3 h-3 ${orderBy === field ? 'opacity-100' : 'opacity-40'}`} />
    </button>
  </TableHead>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PatternUniverse: React.FC<PatternUniverseProps> = ({ patternId, patternName: _patternName, isActive: _isActive }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [universe, setUniverse] = useState<PatternUniverseType | null>(null);
  const [stats, setStats] = useState<PatternUniverseStats | null>(null);
  
  // AI Guard Settings
  const [aiSettings, setAiSettings] = useState<AiGuardSettings | null>(null);
  const [aiUpdating, setAiUpdating] = useState(false);
  const [showAiSettings, setShowAiSettings] = useState(false);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('volume');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUniverse = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await patternsApi.universe(patternId);
      setUniverse(data);
      const statsData = await patternsApi.universeStats(patternId);
      setStats(statsData);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setUniverse(null);
        setStats(null);
      } else {
        setError(err instanceof ApiError ? err.detail : 'Failed to fetch universe');
      }
    } finally {
      setLoading(false);
    }
  }, [patternId]);

  const fetchAiSettings = useCallback(async () => {
    try {
      const res = await settingsApi.aiGuard();
      setAiSettings(res.settings);
    } catch (err) {
      console.error('Failed to fetch AI Guard settings:', err);
    }
  }, []);

  const toggleAiGuard = async () => {
    if (!aiSettings) return;
    setAiUpdating(true);
    try {
      const newSettings = { ...aiSettings, enabled: !aiSettings.enabled };
      await settingsApi.saveAiGuard(newSettings);
      setAiSettings(newSettings);
    } catch (err) {
      console.error('Failed to toggle AI Guard:', err);
    } finally {
      setAiUpdating(false);
    }
  };

  const updateAiMode = async (mode: SignalGuardMode) => {
    if (!aiSettings) return;
    setAiUpdating(true);
    try {
      const newSettings = { ...aiSettings, mode };
      await settingsApi.saveAiGuard(newSettings);
      setAiSettings(newSettings);
    } catch (err) {
      console.error('Failed to update AI Guard mode:', err);
    } finally {
      setAiUpdating(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const data = await patternsApi.universeRefresh(patternId);
      setUniverse(data);
      const statsData = await patternsApi.universeStats(patternId);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Failed to refresh universe');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch on expand
  useEffect(() => {
    if (expanded && !universe && !loading) {
      fetchUniverse();
    }
    fetchAiSettings();
  }, [expanded, universe, loading, fetchUniverse, fetchAiSettings]);

  // Memoized row computations — prevents expensive sorts/filters on every keystroke
  const rows = useMemo<SymbolRow[]>(() =>
    universe?.symbols.map((symbol) => {
      const meta = universe.symbol_metadata[symbol] || {};
      return {
        symbol,
        price:      meta.price      || 0,
        volume:     meta.volume     || 0,
        sector:     meta.sector     || 'Unknown',
        industry:   meta.industry   || 'Unknown',
        market_cap: meta.market_cap || null,
      };
    }) ?? []
  , [universe]);

  const filteredRows = useMemo(() =>
    rows.filter((row) =>
      row.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.sector.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.industry.toLowerCase().includes(searchTerm.toLowerCase())
    )
  , [rows, searchTerm]);

  const sortedRows = useMemo(() =>
    [...filteredRows].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return order === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    })
  , [filteredRows, order, orderBy]);

  const paginatedRows = useMemo(() =>
    sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedRows, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const symbolCount =
    universe?.symbols.length ||
    universe?.target_symbols?.length ||
    0;

  return (
    <div className="space-y-4">
      {/* AI Signal Guard Dashboard */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${aiSettings?.enabled ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                {aiSettings?.enabled ? <ShieldCheck className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  AI SIGNAL GUARD
                  {aiSettings?.enabled && (
                    <span className="flex items-center gap-1 text-[10px] uppercase bg-blue-500 text-white px-1.5 py-0.5 rounded-sm animate-pulse">
                      <Zap className="w-2.5 h-2.5 fill-current" />
                      Live
                    </span>
                  )}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {aiSettings?.enabled ? 'Hybrid Validation Active' : 'AI Guard Disabled (TECHNICAL ONLY)'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={aiSettings?.enabled ? "destructive" : "default"}
                size="sm"
                onClick={toggleAiGuard}
                disabled={aiUpdating || !aiSettings}
                className="font-bold uppercase tracking-tight h-8"
              >
                {aiSettings?.enabled ? 'Kill Switch (OFF)' : 'Activate AI'}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowAiSettings(!showAiSettings)}
              >
                <Settings2 className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>
          </div>

          {aiSettings?.enabled && (
            <div className="grid grid-cols-4 gap-2 pt-1">
              {(['off', 'warming_only', 'scout_only', 'hybrid'] as SignalGuardMode[]).map((m) => (
                <Button
                  key={m}
                  variant={aiSettings.mode === m ? "secondary" : "outline"}
                  size="sm"
                  className={`text-[10px] uppercase h-7 px-1 ${aiSettings.mode === m ? 'border-primary/50 bg-primary/5 shadow-sm font-bold' : 'text-muted-foreground'}`}
                  onClick={() => updateAiMode(m)}
                  disabled={aiUpdating}
                >
                  {m.replace('_', ' ')}
                </Button>
              ))}
            </div>
          )}

          {showAiSettings && aiSettings && (
            <div className="p-3 bg-muted/30 rounded-md border text-xs space-y-2 animate-in fade-in slide-in-from-top-1">
               <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Scout Entry Size</span>
                  <span className="font-mono text-primary font-bold">{aiSettings.scout_size_pct}%</span>
               </div>
               <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Warming Interval</span>
                  <span className="font-mono">{aiSettings.warming_interval_mins}m</span>
               </div>
               <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Confidence Threshold</span>
                  <span className="font-mono">{aiSettings.min_confidence_to_gate}%</span>
               </div>
               <div className="pt-1 text-[10px] text-muted-foreground italic leading-tight">
                  Scout mode enters trades at {aiSettings.scout_size_pct}% size instantly, scaling to 100% after AI confirmation.
               </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 py-1.5 px-3 rounded bg-muted/50 hover:bg-muted transition-colors text-left"
      >
        <Eye className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">Universe</span>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          symbolCount > 0 ? 'bg-blue-500/10 text-blue-700' : 'bg-zinc-500/10 text-zinc-500'
        }`}>
          {symbolCount > 0 ? `${symbolCount} stocks` : 'Not built'}
        </span>
        {universe?.source && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-violet-500/10 text-violet-700">
            {universe.source}
            {typeof universe.credit_cost === 'number' && universe.credit_cost > 0
              ? ` · ${universe.credit_cost} cr`
              : ''}
          </span>
        )}
        {universe?.truncated && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-500/10 text-amber-800">
            capped
          </span>
        )}
        {stats?.last_scan_at && (
          <span className="text-xs text-muted-foreground ml-auto mr-2">
            Scanned {formatDate(stats.last_scan_at)}
          </span>
        )}
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="mt-2 space-y-3">
          {loading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {refreshing && <Progress className="h-1" />}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && !universe && (
            <div className="flex items-center justify-center gap-3 py-4 text-muted-foreground">
              <span className="text-sm">No universe built yet. Click refresh to discover matching stocks.</span>
              <div title="Scan market for matching stocks">
                <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing}>
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          )}

          {!loading && universe && (
            <>
              {/* Stats Row */}
              <div className="flex flex-wrap items-center gap-2">
                {stats && Object.keys(stats.sectors).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(stats.sectors)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 6)
                      .map(([sector, count]) => (
                        <span
                          key={sector}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SECTOR_COLORS[sector] || 'bg-zinc-500/10 text-zinc-700'}`}
                        >{`${sector}: ${count}`}</span>
                      ))}
                    {Object.keys(stats.sectors).length > 6 && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-zinc-500/10 text-zinc-500">
                        {`+${Object.keys(stats.sectors).length - 6} more`}
                      </span>
                    )}
                  </div>
                )}

                {stats?.price_range &&
                  stats.price_range.min != null &&
                  stats.price_range.max != null && (
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/10 text-emerald-700">
                    <TrendingUp className="w-3 h-3" />
                    {`${formatPrice(stats.price_range.min)} - ${formatPrice(stats.price_range.max)}`}
                  </span>
                )}

                <div title="Refresh universe (rescan market)" className="ml-auto">
                  <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing}>
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search symbols, sectors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>

              {/* Symbol Table */}
              <div className="rounded-md border overflow-auto max-h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader field="symbol"     orderBy={orderBy} order={order} onSort={handleSort}>Symbol</SortableHeader>
                      <SortableHeader field="sector"     orderBy={orderBy} order={order} onSort={handleSort}>Sector</SortableHeader>
                      <SortableHeader field="price"      className="text-right" orderBy={orderBy} order={order} onSort={handleSort}>Price</SortableHeader>
                      <SortableHeader field="volume"     className="text-right" orderBy={orderBy} order={order} onSort={handleSort}>Volume</SortableHeader>
                      <SortableHeader field="market_cap" className="text-right" orderBy={orderBy} order={order} onSort={handleSort}>Mkt Cap</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRows.map((row) => (
                      <TableRow key={row.symbol}>
                        <TableCell className="font-medium"><ClickableTicker symbol={row.symbol} /></TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SECTOR_COLORS[row.sector] || 'bg-zinc-500/10 text-zinc-700'}`}>
                            {row.sector}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatPrice(row.price)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.volume)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.market_cap)}</TableCell>
                      </TableRow>
                    ))}
                    {paginatedRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                          {searchTerm ? 'No matching symbols' : 'No symbols in universe'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
                  <div className="flex items-center gap-2">
                    <span>Rows per page:</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      className="bg-transparent border rounded px-1 py-0.5 text-sm"
                    >
                      {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>{page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredRows.length)} of {filteredRows.length}</span>
                    <Button variant="ghost" size="icon" disabled={page === 0} onClick={() => setPage(page - 1)}>
                      <ChevronUp className="w-4 h-4 -rotate-90" />
                    </Button>
                    <Button variant="ghost" size="icon" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                      <ChevronDown className="w-4 h-4 -rotate-90" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default PatternUniverse;
